
import {
  Box,
  Divider,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Pencil, Trash } from "lucide-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  useEffect
} from "react";
import { useTranslation } from "react-i18next";
import {
  AuthWrapper,
  CardProfileBox,
  ContentBox,
  Description,
  InfoTypography,
  TitleTypography
} from "./CardProfile.styles";
import { CardProfileProps } from "./CardProfile.types";
import {
  // Button,
  LanguageModal,
  Link,
  Modal,
  Spinner
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { ProfileMfaConfig } from "../../components/ProfileMfaConfig/ProfileMfaConfig.component";
import {
  useCvFormativeQuery,
  useGetLanguages,
  useGetProfileImageQuery,
  useMutationProfileImageQuery
} from "../../queries";
import { useAuthStore, useLanguageStore } from "../../stores";
import { getError } from "../../utils/error";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";
import { ButtonBookmark } from "../Button";
import { DeleteProfileImageModal } from "../DeleteProfileImageModal";

export function CardProfile({
  birthProvince,
  email,
  location,
  name,
  phone,
  surname
}: CardProfileProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { customLogicFe } = getEnvironmentVariables();

  const authenticationMode = useAuthStore(state => state.mode);
  const userId = useAuthStore(state => state.userData?.id_user);
  const hiddenFileInput = useRef<HTMLInputElement>(null);

  const [modalText, setModalText] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteProfileImageModal, setShowDeleteProfileImageModal] = useState(false);
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  const organizationId = useAuthStore(state => state.userData?.organization_id);
  const initiativeId = useAuthStore(state => state.userData?.initiative_id);
  const userLanguageId = useAuthStore(state => state.userInfo?.language);
  const languageCookie = useLanguageStore(state => state.languageCookie);
  const { data: multiLanguageData } = useGetLanguages({ initiativeId, organizationId, showMainLayout: true });
  const languages = multiLanguageData?.languages ?? [];
  const beLanguage = userLanguageId ? languages.find((lang) => lang.id === userLanguageId) : undefined;
  const currentLanguage = beLanguage ?? languageCookie;

  const { data: profileImage, refetch: refetchProfileImage } = useGetProfileImageQuery({});

  const { data: cvData } = useCvFormativeQuery({
    studentId: userId?.toString() ?? "0"
  });

  const {
    mutate: mutateProfileImage,
    error:mutateProfileError,
    isSuccess:mutateProfileIsSuccess,
    isPending:mutateProfileIsLoading,
    isError : mutateProfileIsError
  } = useMutationProfileImageQuery();

  const acceptFileType = "image/*";

  useEffect(()=> {
    if (mutateProfileIsError && mutateProfileError){
      const errorModal = getError(mutateProfileError)?.[0]?.message;
      setShowModal(true);
      if(errorModal){
        const ismodalForbiddenText = isJSON(errorModal) && JSON.parse(errorModal)?.message === "Forbidden";
        if(ismodalForbiddenText){
          setModalText(t("forbidden_image"));
        }
        else{
          setModalText( errorModal?.toString() ?? "generic error");
        }
      }

    } else if( mutateProfileIsLoading){
      setSpinnerLoading(true);
    } else {
      setSpinnerLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[
    mutateProfileIsError,
    mutateProfileError,
    mutateProfileIsSuccess,
    mutateProfileIsLoading
  ]
  );

  return (
    <>
      <CardProfileBox role="region" aria-label={ t("your_data") }>
        <TitleTypography tabIndex={ 0 } id="title-card-profile">
          { t("your_data") }
        </TitleTypography>
        <ContentBox>
          <Box
            borderRadius= "8px"
            lineHeight= "0.1"
            position= "relative"
            sx={ {
              height: "9rem",
              width: "9rem",
              [theme.breakpoints.down("sm")]:{
                height: "99px",
                width: "106px"
              },
              "> :not(button)":{
                borderRadius: "8px",
                objectFit: "cover",
                width: "100%",
                padding:"0",
                [theme.breakpoints.down("sm")]:{
                  height: "99px",
                  width: "106px"
                }
              }
            } }
          >
            { profileImage && (
              <ButtonBookmark
                aria-label={ t("profile_delete_avatar") }
                onClick={ () => setShowDeleteProfileImageModal(true) }
                variant="contained"
                sx={ {
                  borderRadius: theme.spacing(1),
                  top: theme.spacing(-1),
                  boxShadow: "none",
                  minWidth: "unset",
                  padding: theme.spacing(1),
                  position: "absolute",
                  right: theme.spacing(-1),
                  "svg path":{
                    fill: "transparent !important"
                  },
                  [theme.breakpoints.down("sm")]: {
                    padding: theme.spacing(0.5),
                    top: theme.spacing(-0.5),
                    right: theme.spacing(-0.5)
                  }
                } }
              >
                <Trash
                  size="1rem"
                  fill={ theme.customColors.textPrimaryCta }
                  stroke={ theme.customColors.textPrimaryCta }
                />
              </ButtonBookmark>
            ) }
            {
              spinnerLoading ? (
                <Spinner
                  sx={ {
                    "& circle": {
                      stroke: theme.customColors.accentVariantA
                    },
                    position:"absolute",
                    top: "25%"
                  } }
                />
              ) : (
                profileImage ? (
                  <img
                    src={ profileImage }
                    alt={ `${t("avatar_user")} ${name} ${surname}` }
                    style={ {
                      aspectRatio: "1",
                      backgroundColor: theme.palette.grey?.[400]
                    } }
                    loading="lazy"
                  />
                ) : (
                  <Stack
                    justifyContent="center"
                    sx={ {
                      aspectRatio: "1",
                      backgroundColor: theme.palette.grey?.[400]
                    } }
                  >
                    <Typography
                      aria-label={ `${t("avatar_user")} ${name} ${surname}` }
                      tabIndex={ 0 }
                      fontWeight={ 700 }
                      fontSize={ isMobile ? "3rem" : "5rem" }
                      margin={ theme.spacing(0) }
                      color={ theme.customColors.backgroundPrimaryLighter }
                      textAlign="center"
                      textTransform="uppercase"
                      width="100%"
                    >
                      { name?.charAt(0) + surname?.charAt(0) }
                    </Typography>
                  </Stack>
                )
              )
            }
            <ButtonBookmark
              aria-label={ t("upload_file") }
              onClick={ ()=> handleUploadClick() }
              onKeyDown={ (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleUploadClick();
                }
              } }
              variant="contained"
              sx={ {
                "svg path":{
                  fill: "transparent !important"
                },
                borderRadius: theme.spacing(1),
                bottom: theme.spacing(-1),
                boxShadow: "none",
                minWidth: "unset",
                padding: theme.spacing(1),
                position: "absolute",
                right: theme.spacing(-1),
                [theme.breakpoints.down("sm")]: {
                  padding: theme.spacing(0.5),
                  bottom: theme.spacing(-0.5),
                  right: theme.spacing(-0.5)
                }
              } }
            >
              <input
                aria-label={ t("upload_file") }
                accept={ acceptFileType }
                className="FileUpload"
                type="file"
                ref={ hiddenFileInput }
                onChange={ handleFileChange }
                style={ { display: "none" } }
              />
              <Pencil
                aria-hidden="true"
                color={ theme.customColors.textPrimaryCta }
                size={ "1rem" }
              />
            </ButtonBookmark>
          </Box>
          <Description>
            <TitleTypography
              tabIndex={ 0 }
              aria-label={ `${t("your_name")} ${name} ${" "} ${surname}` }
              fontSize={ "1.25rem" }
              fontWeight={ 700 }
              lineHeight={ "1.5rem" }
              margin={ 0 }
              paddingBottom={ "8px" }
              variant="h1"
              color={ theme.customColors.exploreCardText }
            >
              { name } { surname }
            </TitleTypography>
            <Stack
              flexDirection={ "column" }
              alignItems={ "center" }
              gap="0.25rem"
            >
              {
                email ? (
                  <InfoTypography
                    tabIndex={ 0 }
                    aria-label={ `${t("your_email")} ${email}` }
                    fontSize={ "0.75rem" }
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    color={ theme.customColors.textWidget }
                  >
                    { email }
                  </InfoTypography>
                ) : null
              }
              {
                phone ? (
                  <InfoTypography
                    tabIndex={ 0 }
                  >
                    { phone }
                  </InfoTypography>
                ) : null
              }
              { (birthProvince || location) ? (
                <Box
                  display="flex"
                  flexDirection={ "column" }
                  alignItems={ "center" }
                >

                  <InfoTypography >
                    { location }
                  </InfoTypography>
                  <InfoTypography
                    fontWeight="600 !important"
                  >
                    { birthProvince }
                  </InfoTypography>
                </Box>
              ) : null
              }
            </Stack>
            <AuthWrapper>
              {
                authenticationMode !== "SSO" ? (
                  <Link aria-label={ t("change_password") } href="/access/password/change">
                    { t("change_password") }
                  </Link>
                ) : null
              }
              <ProfileMfaConfig showSeparator={ authenticationMode !== "SSO" } />
            </AuthWrapper>
            { cvData && !checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hide_cv_formativo" }) &&
              <ButtonDigited
                typology="primary"
                buttonSize="md"
                aria-label={ t("download_formative_cv") }
                onClick={ () => {
                  const blob = new Blob([cvData], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `${ name }_${ surname }_cv.csv`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                } }
              >
                { t("download_formative_cv") }
              </ButtonDigited>
            }
            {
              currentLanguage ? (
                <>
                  <Divider sx={ { borderColor: theme.customColors.border, my: "1rem", width: "100%" } } />
                  <Stack direction="row" alignItems="center" gap="0.5rem">
                    <Typography
                      fontSize="0.875rem"
                      color={ theme.customColors.textWidget }
                      sx={ { paddingBottom: "0 !important" } }
                    >
                      { t("default_language") }:{ " " }
                      <Box component="span" fontWeight={ 700 }>{ currentLanguage.label }</Box>
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      aria-label={ t("default_language") }
                      onClick={ () => setShowLanguageModal(true) }
                      sx={ {
                        alignItems: "center",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        display: "inline-flex",
                        p: 0
                      } }
                    >
                      <Pencil size={ 16 } color={ theme.customColors.systemSecondary01 } />
                    </Box>
                  </Stack>
                </>
              ) : null
            }
          </Description>
        </ContentBox>
      </CardProfileBox>
      <Modal
        open={ showModal }
        onClose={ ()=> {
          setShowModal(false);
          setSpinnerLoading(false);
        } }
        description=""
        title={ modalText }
      />
      <DeleteProfileImageModal
        onSuccess={ () => {
          refetchProfileImage();
          if(hiddenFileInput.current) hiddenFileInput.current.value = "";
        } }
        open={ showDeleteProfileImageModal }
        closeDrawer={ () => setShowDeleteProfileImageModal(false) }
      />
      { showLanguageModal &&
        <LanguageModal
          close={ () => setShowLanguageModal(false) }
          languages={ languages }
          open={ showLanguageModal }
        />
      }
    </>
  );

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      const reader = new FileReader();
      reader.readAsDataURL(e.target.files[0]);
      const fileContent = e.target.files[0];
      reader.onload = () => {
        const formdata = new FormData();
        if (fileContent){
          if(fileContent.size > 2000000 ){
            setShowModal(true);
            setModalText(t("file_bigger_text") );
          } else {
            formdata.append("file", fileContent, fileContent.name);
            mutateProfileImage(formdata);
          }
        }
      };
    }
  }

  function handleUploadClick() {
    if (hiddenFileInput.current){
      hiddenFileInput.current.click();
    }
    // if (!file) {
    //   return;
    // }
  }

  function isJSON(str:string) {
    try {
      JSON.stringify(JSON.parse(str));
      return true;
    } catch (e) {
      return false;
    }
  }
}
